import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { CsvImportModal } from '../../components/data/CsvImportModal.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';
import { countBy } from '../../utils/insightStats.js';

import { AccountCreateModal } from './AccountCreateModal.jsx';

const TYPE_ORDER = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

const CSV_FIELDS = [
  { field: 'code', label: 'Code', aliases: ['konto', 'kontonummer', 'nummer'], required: true },
  { field: 'name', label: 'Name', aliases: ['bezeichnung'], required: true },
  { field: 'type', label: 'Typ', aliases: ['type', 'art', 'kontoart'], required: true },
];

// Accepts both the enum values and the German labels used in the UI.
const TYPE_ALIASES = {
  asset: 'ASSET', aktiven: 'ASSET', aktiv: 'ASSET',
  liability: 'LIABILITY', passiven: 'LIABILITY', passiv: 'LIABILITY', fremdkapital: 'LIABILITY',
  equity: 'EQUITY', eigenkapital: 'EQUITY',
  revenue: 'REVENUE', ertrag: 'REVENUE',
  expense: 'EXPENSE', aufwand: 'EXPENSE',
};

export function AccountsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts().then((r) => r.items || []),
  });

  const stats = useMemo(() => {
    const rows = data || [];
    const counts = countBy(rows, (r) => r.type);
    const types = [
      ...TYPE_ORDER.filter((ty) => counts[ty]),
      ...Object.keys(counts).filter((ty) => !TYPE_ORDER.includes(ty)),
    ];
    const active = rows.filter((r) => r.isActive).length;
    return {
      count: rows.length,
      types,
      counts,
      active,
      inactive: rows.length - active,
    };
  }, [data]);

  const columns = [
    { key: 'code', label: t('col.code') },
    { key: 'name', label: t('col.name') },
    {
      key: 'type',
      label: t('col.type'),
      render: (r) => <Badge variant="info">{t(`accountType.${r.type}`)}</Badge>,
    },
    { key: 'balance', label: t('col.balance'), align: 'right', render: (r) => formatCurrency(r.balance) },
    {
      key: 'isActive',
      label: t('col.status'),
      render: (r) => <Badge status={r.isActive ? 'ACTIVE' : 'INACTIVE'} />,
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('finance.accounts.title')}</h1>
          <p className="muted">{t('finance.accounts.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="default" onClick={() => setImportOpen(true)}>↑ {t('csv.import')}</Button>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('finance.accounts.action.new')}
          </Button>
        </div>
      </header>
      {stats.count > 0 && (
        <InsightBar
          title={t('insight.byType')}
          labels={stats.types.map((ty) => (ty === '-' ? ty : t(`accountType.${ty}`)))}
          values={stats.types.map((ty) => stats.counts[ty])}
          metrics={[
            { label: t('insight.count'), value: stats.count },
            { label: t('insight.active'), value: stats.active },
            { label: t('insight.inactive'), value: stats.inactive },
            { label: t('finance.accounts.insight.types'), value: stats.types.length },
          ]}
        />
      )}
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} emptyMessage={t('empty.accounts')} />
      <AccountCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('csv.import')}
        fields={CSV_FIELDS}
        onImportRow={(row) => {
          const normalized = String(row.type || '').trim().toLowerCase();
          const type = TYPE_ALIASES[normalized] || String(row.type || '').trim().toUpperCase();
          return financeApi.createAccount({ code: row.code, name: row.name, type });
        }}
        onDone={() => queryClient.invalidateQueries({ queryKey: ['finance', 'accounts'] })}
      />
    </section>
  );
}
